import MomentUtils from '@date-io/moment';
import CircularProgress from '@material-ui/core/CircularProgress';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ThemeProvider } from '@material-ui/styles';
import { createBrowserHistory } from 'history';
import React, { Suspense, useEffect } from 'react';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { Provider as StoreProvider, useDispatch } from 'react-redux';
import { Router, Route, Switch } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import './assets/css/google.css';
import './assets/scss/main.scss';
import GoogleAnalytics from './components/GoogleAnalytics';
import ScrollReset from './components/ScrollReset';
import StylesProvider from './components/StylesProvider';
import './modules/mixins/chartjs';
import './modules/mixins/moment';
import './modules/mixins/prismjs';
import './modules/mixins/validate';
import { configureStore } from './store';
import { theme } from './modules/theme';
import { publishLocation } from 'src/utils/Common';
import { isMobile, mobileModel } from 'react-device-detect';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import Auth from './layouts/Auth';
import { oktaIssuer, oktaClientId } from 'src/utils/contants';
import { MiddlewareProvider } from './contexts/MiddlewareContext';
import { SnackbarProvider } from 'notistack';
import useOnlineStatus from './hooks/useOnlineStatus';
import useStatusLogger from './hooks/useStatusLogger';
import { statusActions } from './store/slices/statusSlicer';
import './signalr/translation';
import { OfflineDashboardProvider } from './contexts/OfflineDashboardContext';

const { store, persistor } = configureStore();
const history = createBrowserHistory();


const config = {
  issuer: oktaIssuer,
  redirectUri: `${window.location.origin}/implicit/callback`,
  clientId: oktaClientId,
  pkce: true,
  scopes: ['openid', 'email', 'profile'],
  cookies: {
    secure: false
  }
};

const oktaAuth = new OktaAuth(config);

const StatusBootstrap = () => {
  const dispatch = useDispatch();
  const onlineStatus = useOnlineStatus();
  useStatusLogger(onlineStatus.isOnline);

  useEffect(() => {
    dispatch(statusActions.setStatus(onlineStatus.isOnline));
    dispatch(statusActions.setIsOnlineModeActive(onlineStatus.isOnlineModeActive));
  }, [dispatch, onlineStatus.isOnline, onlineStatus.isOnlineModeActive]);

  return null;
};

const restoreOriginalUri = async (_oktaAuth, originalUri) => {
  history.replace(toRelativeUrl(originalUri, window.location.origin));
};


if (isMobile && mobileModel === 'iPhone') {
  setInterval(() => {
    publishLocation(0);
  }, 10000);
}

function App() {
  return (
    <Suspense fallback={null}>
      <StoreProvider store={store}>
        <PersistGate
          loading={(
            <CircularProgress
              status="loading"
            />
          )}
          persistor={persistor}
        >
          <StatusBootstrap />
          <ThemeProvider theme={theme}>
            
            <StylesProvider direction="ltr">
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <Router history={history}>
                  <ScrollReset />
                  <OfflineDashboardProvider>
                    <GoogleAnalytics />
                    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
                      <SnackbarProvider maxSnack={3}>
                        <MiddlewareProvider>
                          <Switch>
                            <Route
                              exact
                              path="/implicit/callback"
                              component={LoginCallback}
                            />
                            <Route path="/" component={Auth} />
                          </Switch>
                        </MiddlewareProvider>
                      </SnackbarProvider>
                    </Security>
                  </OfflineDashboardProvider>
                </Router>
              </MuiPickersUtilsProvider>
            </StylesProvider>
          </ThemeProvider>
        </PersistGate>
      </StoreProvider>
    </Suspense>
  );
}

export default App;
